/**
 * BulkCreateTaskPage — supervisor / contractor_admin creates tasks
 * for every room matching a scope filter (campus → building →
 * optional floor) in one batch.
 *
 * Cicely's Monday-morning use case: "Create a turnover task for every
 * active room in Building X at the Normal tier." The single-task
 * /app/tasks/new form scales linearly (one form-submit per room);
 * this page is the bulk lane.
 *
 * Selection model: filter-based, not per-row checkboxes. The matched
 * room count is computed client-side from the cached useOrgRooms
 * list — no extra DB roundtrip — and shown live as the filter
 * narrows so the operator can sanity-check before submitting.
 *
 * Submit splits the matched rooms into 50-room chunks (matches the
 * Edge Function's MAX_BATCH) and dispatches sequentially so a 200-
 * room building turns into 4 batches under the hood without the
 * operator having to manage that. Aggregate created / failed list
 * displays after the run.
 *
 * Does NOT write to cleaning_tasks directly — every row goes through
 * the bulk-create-cleaning-task Edge Function which delegates to the
 * single-task RPC (CLAUDE.md rule 5).
 */

import { useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useOrgRooms, useOrgCleaners, useOrgCampuses } from '@/hooks/useOrgPickers'
import {
  useBulkCreateCleaningTasks,
  perRowFailedMessage,
  type BulkCreateResult,
  type BulkCreatedRow,
  type BulkFailedRow,
} from '@/hooks/useBulkCreateCleaningTasks'
import type { CycleType, ServiceTier } from '@/hooks/useCreateCleaningTask'
import { isAppError } from '@/lib/errors/AppError'
import { PATHS } from '@/router/paths'

const MAX_BATCH = 50

const CYCLE_TYPES: ReadonlyArray<{ value: CycleType; label: string }> = [
  { value: 'turnover',             label: 'Turnover' },
  { value: 'touchup',              label: 'Touch-up' },
  { value: 'deep_clean',           label: 'Deep clean' },
  { value: 'camp_turn',            label: 'Camp turn' },
  { value: 'maintenance_followup', label: 'Maintenance follow-up' },
]

const SERVICE_TIERS: ReadonlyArray<{ value: ServiceTier; label: string }> = [
  { value: 'touch_up', label: 'Touch up' },
  { value: 'normal',   label: 'Normal' },
  { value: 'heavy',    label: 'Heavy' },
  { value: 'partial',  label: 'Partial unit' },
  { value: 'special',  label: 'Special (one-off)' },
]

interface RoomLike {
  id:          string
  room_number: string
  building?:   { id: string; name: string } | null
  floor?:      { id: string; label: string } | null
}

export function BulkCreateTaskPage(): ReactNode {
  const { t }    = useTranslation()
  const navigate = useNavigate()
  const bulk     = useBulkCreateCleaningTasks()
  const rooms    = useOrgRooms()
  const cleaners = useOrgCleaners()
  const campuses = useOrgCampuses()

  const [campusId,                  setCampusId]                  = useState('')
  const [buildingId,                setBuildingId]                = useState('')
  const [floorId,                   setFloorId]                   = useState('')
  const [cycleType,                 setCycleType]                 = useState<CycleType>('turnover')
  const [serviceTier,               setServiceTier]               = useState<ServiceTier>('normal')
  const [assignedToUserId,          setAssignedToUserId]          = useState('')
  const [estimatedMinutes,          setEstimatedMinutes]          = useState('')
  const [aggregate,                 setAggregate]                 = useState<BulkCreateResult | null>(null)
  const [running,                   setRunning]                   = useState(false)
  const [runError,                  setRunError]                  = useState<string | null>(null)

  // Build unique buildings/floors from the room list within the
  // current selection. Filtering is client-side because the rooms
  // list is already cached and bounded at 2000 entries by
  // useOrgRooms. Memoize the cast so the downstream useMemo deps
  // are referentially stable.
  const allRooms = useMemo<RoomLike[]>(() => (rooms.data ?? []) as RoomLike[], [rooms.data])

  const buildings = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of allRooms) if (r.building) map.set(r.building.id, r.building.name)
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [allRooms])

  const floors = useMemo(() => {
    if (!buildingId) return []
    const map = new Map<string, string>()
    for (const r of allRooms) {
      if (r.building?.id === buildingId && r.floor) map.set(r.floor.id, r.floor.label)
    }
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [allRooms, buildingId])

  const matchedRooms = useMemo(() => {
    return allRooms.filter((r) => {
      if (buildingId && r.building?.id !== buildingId) return false
      if (floorId    && r.floor?.id    !== floorId)    return false
      return true
    })
  }, [allRooms, buildingId, floorId])

  const batchCount = Math.ceil(matchedRooms.length / MAX_BATCH)
  const canSubmit  = matchedRooms.length > 0 && !running && !bulk.isPending

  function handleBuildingChange(next: string): void {
    setBuildingId(next)
    setFloorId('') // reset floor when building changes
  }

  function handleTierChange(next: ServiceTier): void {
    setServiceTier(next)
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (matchedRooms.length === 0) return

    setRunError(null)
    setAggregate(null)
    setRunning(true)

    const minutesNum = estimatedMinutes.trim().length > 0
      ? Number.parseInt(estimatedMinutes, 10)
      : null

    const created: BulkCreatedRow[] = []
    const failed:  BulkFailedRow[]  = []
    const chunks = chunk(matchedRooms.map((r) => r.id), MAX_BATCH)

    try {
      for (const ids of chunks) {
        const result = await bulk.mutateAsync({
          roomIds:                 ids,
          cycleType,
          serviceTier,
          assignedToUserId:        assignedToUserId || null,
          estimatedMinutes:        Number.isFinite(minutesNum) && minutesNum !== null && minutesNum > 0
                                     ? minutesNum
                                     : null,
        })
        created.push(...result.created)
        failed .push(...result.failed)
      }
      setAggregate({ created, failed })
    } catch (err) {
      if (isAppError(err)) setRunError(err.userMessage)
      else                 setRunError('Bulk create hit an unexpected error. Try again.')
    } finally {
      setRunning(false)
    }
  }

  return (
    <article style={{ maxWidth: '42rem', margin: '0 auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }}>
          {t('bulkTask.title', 'Bulk create tasks')}
        </h1>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--brand-neutral-500)', fontSize: '0.9rem' }}>
          {t(
            'bulkTask.subtitle',
            'Create one task per room matching the scope below. Up to 50 rooms per server batch; larger selections are dispatched in multiple batches automatically.',
          )}
        </p>
      </header>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }} aria-busy={running}>
        <Field label={t('bulkTask.campus', 'Campus (optional filter)')}>
          <select
            data-testid="bct-campus"
            value={campusId}
            onChange={(e) => setCampusId(e.target.value)}
            disabled={running || campuses.isLoading}
            style={inputStyle}
          >
            <option value="">{t('bulkTask.anyCampus', 'Any campus')}</option>
            {(campuses.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>

        <Field label={t('bulkTask.building', 'Building')}>
          <select
            data-testid="bct-building"
            value={buildingId}
            onChange={(e) => handleBuildingChange(e.target.value)}
            disabled={running || rooms.isLoading}
            style={inputStyle}
          >
            <option value="">{rooms.isLoading ? t('bulkTask.loading', 'Loading…') : t('bulkTask.anyBuilding', 'Any building')}</option>
            {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Field>

        <Field label={t('bulkTask.floor', 'Floor (optional)')}>
          <select
            data-testid="bct-floor"
            value={floorId}
            onChange={(e) => setFloorId(e.target.value)}
            disabled={running || !buildingId || floors.length === 0}
            style={inputStyle}
          >
            <option value="">{buildingId ? t('bulkTask.anyFloor', 'Any floor') : t('bulkTask.pickBuildingFirst', 'Pick a building first')}</option>
            {floors.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </Field>

        <Field label={t('bulkTask.cycle', 'Cycle type')}>
          <select
            data-testid="bct-cycle"
            value={cycleType}
            onChange={(e) => setCycleType(e.target.value as CycleType)}
            disabled={running}
            style={inputStyle}
            required
          >
            {CYCLE_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>

        <Field label={t('bulkTask.tier', 'Service tier')}>
          <select
            data-testid="bct-tier"
            value={serviceTier}
            onChange={(e) => handleTierChange(e.target.value as ServiceTier)}
            disabled={running}
            style={inputStyle}
            required
          >
            {SERVICE_TIERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Field>

        <Field label={t('bulkTask.assignee', 'Assign all to (optional)')}>
          <select
            data-testid="bct-assignee"
            value={assignedToUserId}
            onChange={(e) => setAssignedToUserId(e.target.value)}
            disabled={running || cleaners.isLoading}
            style={inputStyle}
          >
            <option value="">{t('bulkTask.unassigned', 'Unassigned — assign later per room')}</option>
            {(cleaners.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.full_name} ({c.role})</option>
            ))}
          </select>
        </Field>

        <Field label={t('bulkTask.estimatedMinutes', 'Estimated minutes per task (optional)')}>
          <input
            data-testid="bct-estimate"
            type="number"
            inputMode="numeric"
            min={1}
            max={1440}
            value={estimatedMinutes}
            onChange={(e) => setEstimatedMinutes(e.target.value)}
            disabled={running}
            placeholder="e.g. 45"
            style={inputStyle}
          />
        </Field>

        <div
          data-testid="bct-summary"
          style={{
            background:   'var(--brand-primary-subtle)',
            color:        'var(--brand-neutral-900)',
            borderLeft:   '3px solid var(--brand-primary)',
            borderRadius: 'var(--brand-radius-sm)',
            padding:      '0.5rem 0.75rem',
            fontSize:     '0.9rem',
          }}
        >
          {matchedRooms.length === 0
            ? t('bulkTask.summaryEmpty', 'No rooms match the current scope. Adjust the filters.')
            : t(
                'bulkTask.summaryMatch',
                'Will create {{count}} task(s) across {{batches}} batch(es) of up to {{batchSize}} rooms each.',
                { count: matchedRooms.length, batches: batchCount, batchSize: MAX_BATCH },
              )}
        </div>

        {runError && (
          <p role="alert" data-testid="bct-error" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem', margin: 0 }}>
            {runError}
          </p>
        )}

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="submit"
            data-testid="bct-submit"
            disabled={!canSubmit}
            style={{
              background:    'var(--brand-primary)',
              color:         'var(--brand-white)',
              border:        'none',
              borderRadius:  'var(--brand-radius-md)',
              padding:       '0.6rem 1.2rem',
              fontSize:      '0.95rem',
              fontWeight:    600,
              cursor:        canSubmit ? 'pointer' : 'default',
              opacity:       canSubmit ? 1 : 0.6,
            }}
          >
            {running
              ? t('bulkTask.creating', 'Creating…')
              : t('bulkTask.submit', `Create ${matchedRooms.length} task${matchedRooms.length === 1 ? '' : 's'}`)}
          </button>
          <button
            type="button"
            onClick={() => navigate(PATHS.dashboard)}
            data-testid="bct-cancel"
            disabled={running}
            style={{
              background:    'transparent',
              color:         'var(--brand-neutral-700)',
              border:        '1px solid var(--brand-neutral-300)',
              borderRadius:  'var(--brand-radius-md)',
              padding:       '0.6rem 1.2rem',
              fontSize:      '0.95rem',
              cursor:        running ? 'default' : 'pointer',
            }}
          >
            {t('bulkTask.cancel', 'Cancel')}
          </button>
        </div>
      </form>

      {aggregate && (
        <section
          data-testid="bct-result"
          style={{
            background:   'var(--brand-white)',
            border:       '1px solid var(--brand-neutral-300)',
            borderRadius: 'var(--brand-radius-lg)',
            padding:      '1rem',
            display:      'flex',
            flexDirection:'column',
            gap:          '0.5rem',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1rem' }}>
            {t('bulkTask.resultTitle', 'Result')}
          </h2>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            <strong>{aggregate.created.length}</strong> {t('bulkTask.created', 'created')}
            {' · '}
            <strong>{aggregate.failed.length}</strong> {t('bulkTask.failed', 'failed')}
          </p>
          {aggregate.failed.length > 0 && (
            <ul data-testid="bct-failed-list" style={{ margin: 0, padding: '0 0 0 1rem' }}>
              {aggregate.failed.map((f) => (
                <li key={f.room_id} style={{ fontSize: '0.85rem', color: 'var(--brand-danger)' }}>
                  Room {shortId(f.room_id)} — {perRowFailedMessage(f)}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </article>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
      {label}
      {children}
    </label>
  )
}

const inputStyle: React.CSSProperties = {
  fontSize:     '0.95rem',
  padding:      '0.45rem 0.55rem',
  borderRadius: 'var(--brand-radius-sm)',
  border:       '1px solid var(--brand-neutral-300)',
  background:   'var(--brand-white)',
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id
}
